import { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
export const KeyboardModeContext = /*#__PURE__*/createContext({
  isKeyboardMode: false
});
const HIDE_FOCUS_STYLES_CLASS = 'hubspot-disable-focus-styles';
const SHOW_FOCUS_STYLES_CLASS = 'hubspot-enable-focus-styles';

// Export constants for backward compatibility
export { HIDE_FOCUS_STYLES_CLASS, SHOW_FOCUS_STYLES_CLASS };

// Global state to track keyboard mode for non-React contexts
let globalKeyboardMode = false;
export const KeyboardModeProvider = ({
  children,
  targetElement = document.body
}) => {
  // Indicates if the user is in keyboard mode or mouse mode.
  const [isKeyboardMode, setIsKeyboardMode] = useState(false);
  const value = useMemo(() => ({
    isKeyboardMode
  }), [isKeyboardMode]);

  /**
   * Listens for keyboard and mouse events to determine if the user is in keyboard mode.
   * Also manages CSS classes on document.body for backward compatibility with the old UIComponents library.
   */
  useEffect(() => {
    const containerElement = targetElement;
    const activateKeyboardMode = () => {
      globalKeyboardMode = true;
      setIsKeyboardMode(true);

      // Add CSS classes for backward compatibility
      containerElement.classList.add(SHOW_FOCUS_STYLES_CLASS);
      containerElement.classList.remove(HIDE_FOCUS_STYLES_CLASS);
    };
    const activateMouseMode = () => {
      globalKeyboardMode = false;
      setIsKeyboardMode(false);

      // Add CSS classes for backward compatibility
      containerElement.classList.add(HIDE_FOCUS_STYLES_CLASS);
      containerElement.classList.remove(SHOW_FOCUS_STYLES_CLASS);
    };
    containerElement.addEventListener('keydown', activateKeyboardMode);
    containerElement.addEventListener('mousemove', activateMouseMode);
    containerElement.addEventListener('mousedown', activateMouseMode);
    containerElement.addEventListener('mouseup', activateMouseMode);

    // Initialize with mouse mode (matching old library behavior)
    activateMouseMode();
    return () => {
      containerElement.removeEventListener('keydown', activateKeyboardMode);
      containerElement.removeEventListener('mousemove', activateMouseMode);
      containerElement.removeEventListener('mousedown', activateMouseMode);
      containerElement.removeEventListener('mouseup', activateMouseMode);

      // Cleanup: remove classes on unmount
      containerElement.classList.remove(HIDE_FOCUS_STYLES_CLASS);
      containerElement.classList.remove(SHOW_FOCUS_STYLES_CLASS);
    };
  }, [targetElement]);
  return /*#__PURE__*/_jsx(KeyboardModeContext.Provider, {
    value: value,
    children: children
  });
};

/**
 * Indicates if the user is in keyboard mode or mouse mode.
 * @returns isKeyboardMode - Boolean indicating if the user is in keyboard mode.
 */
export const useIsKeyboardMode = () => {
  const {
    isKeyboardMode
  } = useContext(KeyboardModeContext);
  return isKeyboardMode;
};

/**
 * Backward-compatible function to check if the page is in keyboard mode.
 * This provides the same API as the old UIComponents library.
 * @returns {boolean} Was the most recently detected input event a keyboard event?
 */
export const isPageInKeyboardMode = () => {
  return globalKeyboardMode;
};